import { observable, action, computed } from 'mobx';
import axios from 'axios';
import BudgetApi from 'api/budget';
import BudgetItemApi from 'api/budget-item';
import TransactionItemApi from 'api/transaction-item';

class BudgetStore {
    @observable loading = true;
    @observable year;
    @observable month;

    @observable budgeted;
    @observable actual;
    @observable.shallow categories = [];

    @computed get remaining() {
        return this.budgeted - this.actual;
    }

    constructor(rootStore) {
        this.rootStore = rootStore;

        // Set month and year to current
        const today = new Date();
        this.year = today.getFullYear();
        this.month = today.getMonth()+1;
    }

    /**
     * Request selected month budget from the API
     */
    @action
    requestBudget() {
        this.loading = true;

        if (typeof this._myBudgetSource !== typeof undefined) {
            this._myBudgetSource.cancel('Cancelled due to new request');
        }

        this._myBudgetSource = axios.CancelToken.source();

        const q = BudgetApi.getMyBudget(this.year, this.month, this._myBudgetSource.token);

        q.then(response => {
            this.setBudget(response.data);
        });

        // TODO: Nice error

        return q;
    }

    /**
     * Send request to API to copy last month's budget
     */
    @action
    copyLastMonth() {
        this.loading = true;
        this.budget = null;

        if (typeof this._myBudgetSource !== typeof undefined) {
            this._myBudgetSource.cancel('Cancelled due to new request');
        }

        this._myBudgetSource = axios.CancelToken.source();

        const q = BudgetApi.copyLastMonth(this.year, this.month, this._myBudgetSource.token);
        q.then(response => this.setBudget(response.data));

        // TODO: Catch errors

        return q;
    }

    /**
     * Assign budget retrieved from the API
     */
    @action.bound
    setBudget(budget) {
        this.actual = budget.actual;
        this.budgeted = budget.budgeted;

        const store = this;
        this.categories = budget.categories.map(({id, name, budgeted, actual, categories}) => {
            const bigCategory = new BigCategory(id, name, budgeted, actual);
            bigCategory.setStore(store);
            bigCategory.setCategories(categories.map(({id, name, budgeted, actual, budgetItemId}) => {
                const smallCategory = new SmallCategory(id, name, budgeted, actual, budgetItemId);
                smallCategory.setParent(bigCategory);
                return smallCategory;
            }));
            return bigCategory;
        });

        this.loading = false;
    }

    @action
    changeDate(year, month) {
        this.year = year;
        this.month = month;

        this.requestBudget();
    }

    @action
    requestAddBudgetItem(budgetItem) {
        const q = BudgetItemApi.post({
            year: this.year,
            month: this.month,
            amount: budgetItem.amount,
            category: budgetItem.category,
        });
        q.then(response => this.requestAddBudgetItemSuccess(response.data));
        return q;
    }

    @action.bound
    requestAddBudgetItemSuccess(budgetItem) {
        const category = budgetItem.category;
        const parentCategory = category.category || category;

        const bigCategory = this.createOrFindBigCategory(parentCategory);
        const smallCategory = bigCategory.createOrFindSmallCategory(category);
        smallCategory.addBudgetItemSuccess(budgetItem);
    }

    /**
     * Send a request to API asking to add a new transaction item
     */
    @action
    requestAddTransaction(transaction) {
        const q = TransactionItemApi.post(transaction);
        q.then(response => this.requestAddTransactionSuccess(response.data));
        return q;
    }

    @action.bound
    requestAddTransactionSuccess(transaction) {
        const category = transaction.category;
        const parentCategory = category.category || category;

        const bigCategory = this.createOrFindBigCategory(parentCategory);
        const smallCategory = bigCategory.createOrFindSmallCategory(category);
        smallCategory.addTransactionSuccess(transaction);
    }

    @action
    createOrFindBigCategory(category) {
        let bigCategory = this.categories.find(c => c.id === category.id);
        if (bigCategory) {
            return bigCategory;
        }

        // Create if not found
        const { id, name } = category;
        bigCategory = new BigCategory(id, name, 0, 0);
        bigCategory.setStore(this);
        this.pushBigCategory(bigCategory);
        return bigCategory;
    }

    @action
    pushBigCategory(category) {
        // Find alphabetical position for a new category
        const index = this.categories.findIndex(item => category.name < item.name);
        if (index === -1) {
            // Add this to the back
            this.categories.push(category);
        } else {
            this.categories.splice(index, 0, category);
        }
    }
}

export class BigCategory {
    @observable loading;
    id;
    name;
    @observable budgeted;
    @observable actual;
    @observable.shallow categories = [];
    store;

    @computed get remaining() {
        return this.budgeted - this.actual;
    }

    constructor(id, name, budgeted, actual) {
        this.id = id;
        this.name = name;
        this.budgeted = budgeted;
        this.actual = actual;
    }

    setStore(store) {
        this.store = store;
    }

    @action
    setCategories(categories) {
        this.categories = categories;
    }

    @action
    createOrFindSmallCategory(category) {
        let smallCategory = this.categories.find(c => c.id === category.id);
        if (smallCategory) {
            return smallCategory;
        }

        // Create if not found
        const { id, name } = category;
        smallCategory = new SmallCategory(id, name, 0, 0);
        smallCategory.setParent(this);
        this.pushSmallCategory(smallCategory);
        return smallCategory;
    }

    @action
    pushSmallCategory(category) {
        // Find alphabetical position for a new category
        const index = this.categories.findIndex(item => category.name < item.name);
        if (index === -1) {
            // Add this to the back
            this.categories.push(category);
        } else {
            this.categories.splice(index, 0, category);
        }
    }
}

/**
 * SmallCategory which is a child of BigCategory
 */
export class SmallCategory {
    @observable loading = false;
    id;
    name;
    @observable budgeted;
    @observable actual;
    @observable.shallow transactions = [];
    @observable budgetItemId;
    parent;

    @computed get remaining() {
        return this.budgeted - this.actual;
    }

    constructor(id, name, budgeted, actual, budgetItemId = null) {
        this.id = id;
        this.name = name;
        this.budgeted = budgeted;
        this.actual = actual;
        this.budgetItemId = budgetItemId;
    }

    setParent(parent) {
        this.parent = parent;
    }

    @action
    requestEdit(budgetItem) {
        this.loading = true;

        const q = BudgetItemApi.put(this.budgetItemId, budgetItem);
        q.then(response => this.editSuccess(response.data));
        // TODO: Nice error
        return q;
    }

    @action.bound
    editSuccess(budgetItem) {
        const diff = budgetItem.amount - this.budgeted;

        // Recalculate everything
        this.budgeted = budgetItem.amount;
        this.parent.budgeted += diff;
        this.parent.store.budgeted += diff;

        this.loading = false;
    }

    @action
    requestDelete(includeTransactions) {
        this.loading = true;

        const q = BudgetItemApi.delete(this.budgetItemId, includeTransactions);

        q.then(response => {
            // If we're deleting transactions or this item does not have any transactions
            if (includeTransactions || this.actual === 0) {
                // Delete this line completely
                this.deleteSuccess(true);
            } else {
                // If it includes transactions, then we only need to update it
                this.deleteSuccess();
            }
        });

        return q;
    }

    @action.bound
    deleteSuccess(includeTransactions = false) {
        if (includeTransactions) {
            // Remove whole line including transactions
            this.parent.budgeted -= this.budgeted;
            this.parent.store.budgeted -= this.budgeted;
            this.parent.actual -= this.actual;
            this.parent.store.actual -= this.actual;

            this.parent.categories.remove(this);

            // Check whether to remove big category also
            if (this.parent.categories.length === 0) {
                this.parent.store.categories.remove(this.parent);
            }
        } else {
            // Only make this unbudgeted
            this.parent.budgeted -= this.budgeted;
            this.parent.store.budgeted -= this.budgeted;
            this.budgeted = 0;
            this.budgetItemId = null;
        }
    }

    @action.bound
    addBudgetItemSuccess(budgetItem) {
        const { id, amount } = budgetItem;
        const diff = amount - this.budgeted;

        // Recalculate everything
        this.budgeted = amount;
        this.parent.budgeted += diff;
        this.parent.store.budgeted += diff;

        this.budgetItemId = id;
        this.loading = false;
    }

    @action.bound
    addTransactionSuccess(transaction) {
        const { id, amount, date, description } = transaction;
        const transactionItem = new TransactionItem(id, amount, date, description);
        transactionItem.setParent(this);
        this.transactions.push(transactionItem);

        // Calculate everything
        this.actual += amount;
        this.parent.actual += amount;
        this.parent.store.actual += amount;

        this.loading = false;
    }

    @action
    requestTransactions() {
        this.loading = true;

        const q = TransactionItemApi.list({
            category: this.id,
            year: this.parent.store.year,
            month: this.parent.store.month,
        });
        q.then(this.requestTransactionsSuccess);
        return q;
    }

    @action.bound
    requestTransactionsSuccess(transactions) {
        this.loading = false;
        this.transactions = transactions.map(t => {
            const { id, amount, date, description } = t;
            const transaction = new TransactionItem(id, amount, date, description);
            transaction.setParent(this);
            return transaction;
        });
    }
}

/**
 * TransactionItem which is a child of SmallCategory
 */
export class TransactionItem {
    @observable loading = false;
    id;
    @observable amount;
    @observable date;
    @observable description;
    parent;

    constructor(id, amount, date, description) {
        this.id = id;
        this.amount = amount;
        this.date = date;
        this.description = description;
    }

    setParent(parent) {
        this.parent = parent;
    }

    @action
    requestEdit(transaction) {
        this.loading = true;

        const q = TransactionItemApi.put(this.id, transaction);
        q.then(response => this.editSuccess(response.data));
        return q;
    }

    @action.bound
    editSuccess(transaction) {
        const diff = transaction.amount - this.amount;

        // Recalculate everything
        this.amount = transaction.amount;
        this.parent.actual += diff;
        this.parent.parent.actual += diff;
        this.parent.parent.store.actual += diff;

        this.loading = false;
    }

    @action
    requestDelete() {
        this.loading = true;
        const q = TransactionItemApi.delete(this.id);
        q.then(this.deleteSuccess);
        // TODO: Nice error
        return q;
    }

    @action.bound
    deleteSuccess() {
        // Recalculate everything
        const amount = this.amount;
        this.parent.actual -= amount;
        this.parent.parent.actual -= amount;
        this.parent.parent.store.actual -= amount;

        this.parent.transactions.remove(this);
    }
}

export default BudgetStore;


// WEBPACK FOOTER //
// ./src/store/budgetStore.js